import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Zap, X, Crown, AlertTriangle, RefreshCw } from 'lucide-react';
import { useSubscription } from '../../context/SubscriptionContext';
import { useAuth } from '../../context/AuthContext';
import { isIOS } from '../../services/native';

export default function TrialBanner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    isTrialing,
    isActive,
    isExpired,
    trialDaysLeft,
    subscriptionDaysLeft,
    freeAccessMode,
    hidePaymentUI
  } = useSubscription();
  const [dismissed, setDismissed] = useState(false);

  // Don't show banner in free access mode, for admins, or if dismissed
  if (freeAccessMode || user?.isAdmin || dismissed) return null;

  // Expired banner (trial or subscription)
  if (isExpired) {
    return (
      <div className="bg-red-500 text-white px-3 py-2" style={isIOS ? { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)' } : undefined}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <p className="text-sm font-medium">
              <span className="hidden sm:inline">{hidePaymentUI ? 'Your subscription has ended.' : 'Your subscription has expired. Subscribe to continue using all features.'}</span>
              <span className="sm:hidden">Subscription expired</span>
            </p>
          </div>
          {!hidePaymentUI && (
            <button
              onClick={() => navigate('/pricing')}
              className="flex-shrink-0 px-3 py-1 bg-white text-red-600 rounded-lg text-sm font-semibold hover:bg-red-50 transition-colors"
            >
              Subscribe
            </button>
          )}
        </div>
      </div>
    );
  }

  // Trial banner — show 5 days or fewer before trial ends
  if (isTrialing) {
    if (trialDaysLeft > 5) return null;

    const isUrgent = trialDaysLeft <= 3;
    const bgColor = isUrgent ? 'bg-amber-500' : 'bg-primary-600';

    const getShortMessage = () => {
      if (trialDaysLeft === 0) return 'Trial ends today';
      if (trialDaysLeft === 1) return '1 day left';
      return `${trialDaysLeft} days left`;
    };

    const getFullMessage = () => {
      if (trialDaysLeft === 0) return hidePaymentUI ? 'Your trial ends today.' : 'Your trial ends today! Subscribe to keep access.';
      if (trialDaysLeft === 1) return 'Only 1 day left in your trial!';
      if (isUrgent) return `Only ${trialDaysLeft} days left in your trial!`;
      return hidePaymentUI ? `${trialDaysLeft} days left in trial.` : `${trialDaysLeft} days left in trial. Subscribe anytime.`;
    };

    return (
      <div className={`${bgColor} text-white px-3 py-2 relative`} style={isIOS ? { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)' } : undefined}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            {isUrgent ? (
              <Clock className="w-4 h-4 flex-shrink-0 animate-pulse" />
            ) : (
              <Crown className="w-4 h-4 flex-shrink-0" />
            )}
            <p className="text-sm font-medium">
              <span className="hidden sm:inline">{getFullMessage()}</span>
              <span className="sm:hidden">{getShortMessage()}</span>
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {!hidePaymentUI && (
              <button
                onClick={() => navigate('/pricing')}
                className="px-2.5 py-1 bg-white text-primary-600 rounded-lg text-xs sm:text-sm font-semibold hover:bg-primary-50 transition-colors flex items-center gap-1"
              >
                <Zap className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                <span className="hidden sm:inline">Subscribe</span>
                <span className="sm:hidden">Upgrade</span>
              </button>
            )}
            <button
              onClick={() => setDismissed(true)}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Renewal banner — show 10 days or fewer before active subscription renews
  if (isActive && subscriptionDaysLeft > 0 && subscriptionDaysLeft <= 10) {
    const isUrgent = subscriptionDaysLeft <= 3;
    const bgColor = isUrgent ? 'bg-amber-500' : 'bg-blue-600';

    const getShortMessage = () => {
      if (subscriptionDaysLeft === 1) return 'Renews tomorrow';
      return `Renews in ${subscriptionDaysLeft} days`;
    };

    const getFullMessage = () => {
      if (subscriptionDaysLeft === 1) return 'Your subscription renews tomorrow. Ensure your payment method is up to date.';
      if (isUrgent) return `Subscription renews in ${subscriptionDaysLeft} days. Check your payment method.`;
      return `Subscription renews in ${subscriptionDaysLeft} days.`;
    };

    return (
      <div className={`${bgColor} text-white px-3 py-2 relative`} style={isIOS ? { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)' } : undefined}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <RefreshCw className="w-4 h-4 flex-shrink-0" />
            <p className="text-sm font-medium">
              <span className="hidden sm:inline">{getFullMessage()}</span>
              <span className="sm:hidden">{getShortMessage()}</span>
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {!hidePaymentUI && (
              <button
                onClick={() => navigate('/pricing')}
                className="px-2.5 py-1 bg-white text-blue-600 rounded-lg text-xs sm:text-sm font-semibold hover:bg-blue-50 transition-colors flex items-center gap-1"
              >
                <span>Manage</span>
              </button>
            )}
            <button
              onClick={() => setDismissed(true)}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
